// Month calendar of money entries (Income, Expenses): same look as
// Receivable / Payable's calendar. Each item sits on its day as a small
// coloured chip (name + amount); a day shows its total; click a chip to
// open the entry. Day keys are LOCAL dates (never toISOString, which put
// entries a day late in time zones ahead of UTC).

import { useMemo } from 'react';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import { localISO } from '../utils/localDate';

export type CalTone = 'in' | 'paid' | 'due' | 'late';

export interface CalItem {
  id:      string;
  date:    string;   // YYYY-MM-DD, the day it sits on
  label:   string;
  amount:  number;
  currency?: string;
  tone:    CalTone;
  title?:  string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TONE: Record<CalTone, { bg: string; color: string; border: string; label: string }> = {
  in:   { bg: 'var(--success-bg)', color: 'var(--success)', border: 'color-mix(in srgb, var(--success) 30%, transparent)', label: 'Money in' },
  paid: { bg: 'var(--primary-light)', color: 'var(--primary)', border: 'color-mix(in srgb, var(--primary) 25%, transparent)', label: 'Paid' },
  due:  { bg: 'var(--warning-bg)', color: '#c2410c', border: 'color-mix(in srgb, var(--warning) 40%, transparent)', label: 'To pay' },
  late: { bg: 'var(--danger-bg)', color: 'var(--danger)', border: 'color-mix(in srgb, var(--danger) 30%, transparent)', label: 'Overdue' },
};


function monthDays(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const out: (Date | null)[] = [];
  for (let i = 0; i < first.getDay(); i++) out.push(null);
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d++) out.push(new Date(year, month, d));
  while (out.length % 7 !== 0) out.push(null);
  return out;
}

function compact(v: number, cur: string) {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${fmtMoney(v / 1_000_000, cur).replace(/\.00$/, '')}M`;
  if (abs >= 10_000) return `${fmtMoney(Math.round(v / 1000), cur).replace(/\.00$/, '')}k`;
  return fmtMoney(v, cur).replace(/\.00$/, '');
}

export function MoneyCalendar({ items, year, month, onPrev, onNext, onSelect, tones }: {
  items: CalItem[];
  year: number;
  month: number;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (id: string) => void;
  /** Which colours to explain in the legend. */
  tones: CalTone[];
  /** Kept for callers; the line is just "$4,200 (6 entries)" now. */
  summary?: string;
}) {
  const settings = useSettings();
  const cur = settings.currency;
  const today = localISO(new Date());
  const days = monthDays(year, month);

  const byDate = useMemo(() => {
    const m: Record<string, CalItem[]> = {};
    items.forEach(it => {
      const [y, mo] = it.date.split('-').map(Number);
      if (y === year && mo - 1 === month) (m[it.date] ??= []).push(it);
    });
    return m;
  }, [items, year, month]);
  const inMonth = Object.values(byDate).flat();
  const monthTotal = inMonth.reduce((s, it) => s + it.amount, 0);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="mcal-head">
        <div className="mcal-step">
          <button type="button" onClick={onPrev} aria-label="Previous month">‹</button>
          <span>{MONTHS[month]} {year}</span>
          <button type="button" onClick={onNext} aria-label="Next month">›</button>
        </div>
        {inMonth.length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{fmtMoney(monthTotal, cur)}</strong>
            {' '}({inMonth.length} {inMonth.length === 1 ? 'entry' : 'entries'})
          </div>
        )}
        <div className="mcal-legend">
          {tones.map(t => (
            <span key={t}><i style={{ background: TONE[t].bg, borderColor: TONE[t].border }} />{TONE[t].label}</span>
          ))}
        </div>
      </div>

      <div className="mcal-grid mcal-days">
        {DAYS.map((d, i) => <div key={d} style={{ color: i === 0 || i === 6 ? 'var(--text-3)' : 'var(--text-2)' }}>{d}</div>)}
      </div>

      <div className="mcal-grid">
        {days.map((day, i) => {
          const edge = { borderRight: (i + 1) % 7 === 0 ? 'none' : '1px solid var(--border-2)', borderBottom: i < days.length - 7 ? '1px solid var(--border-2)' : 'none' };
          if (!day) return <div key={`e${i}`} className="mcal-cell mcal-empty" style={edge} />;
          const key = localISO(day);
          const list = byDate[key] ?? [];
          const isToday = key === today;
          const weekend = day.getDay() === 0 || day.getDay() === 6;
          const dayTotal = list.reduce((s, it) => s + it.amount, 0);
          return (
            <div key={key} className={`mcal-cell${isToday ? ' today' : weekend ? ' weekend' : ''}`} style={edge}>
              <div className="mcal-cell-top">
                <span className="mcal-num">{day.getDate()}</span>
                {list.length > 0 && <span className="mcal-total">{compact(dayTotal, cur)}</span>}
              </div>
              {list.slice(0, 3).map(it => {
                const t = TONE[it.tone];
                return (
                  <button key={it.id} type="button" className="mcal-chip" onClick={() => onSelect(it.id)}
                    title={it.title ?? `${it.label} · ${fmtMoney(it.amount, it.currency ?? cur)}`}
                    style={{ background: t.bg, color: t.color, borderColor: t.border }}>
                    <span className="mcal-chip-name">{it.label}</span>
                    <span>{compact(it.amount, it.currency ?? cur)}</span>
                  </button>
                );
              })}
              {list.length > 3 && <div className="mcal-more">+{list.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The List / Calendar switch for the page header (same look as Receivable's). */
export function ViewToggle({ value, onChange }: { value: 'list' | 'calendar'; onChange: (v: 'list' | 'calendar') => void }) {
  return (
    <div className="tb-seg" role="tablist" aria-label="View">
      {(['list', 'calendar'] as const).map(mode => (
        <button key={mode} type="button" role="tab" aria-selected={value === mode}
          className={value === mode ? 'on' : undefined} onClick={() => onChange(mode)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {mode === 'list' ? (
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M4.5 3.5h7M4.5 7h7M4.5 10.5h7M2 3.5h.01M2 7h.01M2 10.5h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M1.5 5.5h11M4.5 1.2v2.4M9.5 1.2v2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          )}
          {mode === 'list' ? 'List' : 'Calendar'}
        </button>
      ))}
    </div>
  );
}
